import {
  ReportingComparisonDatasets,
  ReportingComparisonScopes,
  type ReportingComparisonDataset,
  type ReportingComparisonScope,
} from '../enums/comparison';
import type { ReportingComparisonDatasetDto, ReportingComparisonSummaryDto } from '../response/comparison';
import {
  formatComparisonCurrency,
  formatComparisonDecimal,
  formatComparisonDeltaCurrency,
  formatComparisonDeltaNumber,
  formatComparisonPercent,
  getComparisonMetricCaption,
  getComparisonPercentCaption,
  getComparisonTone,
  type ComparisonRanges,
  type ComparisonTone,
} from './comparison-format';

/**
 * Every table the Comparison report can show, described once.
 *
 * The page declares its grids from this, and the reporting API reads a viewer's arrangement
 * against it when it prints one. A preset only stores what a viewer changed about a grid — the
 * rest is whatever the grid ships with — so anything that is to read a preset without the page
 * in front of it needs to know what the grids ship with. This is that, and it is the only copy:
 * two copies would each describe a different page the first time one of them moved a column.
 */

export type ComparisonColumnKind = 'text' | 'currency' | 'decimal' | 'percent';
export type ComparisonTableKind = 'overall' | 'mover' | 'all';
export type ComparisonTableLevel = 'mga' | 'agent';
export type ComparisonTableDirection = 'increase' | 'decrease';

/** Every column a comparison grid can carry, in the order every grid declares them. */
export const COMPARISON_COLUMN_FIELDS = [
  'entityName',
  'mgaName',
  'goalAmount',
  'goalAttainedPercent',
  'currentPremium',
  'comparisonPremium',
  'premiumDifference',
  'premiumChangePercent',
  'currentPolicies',
  'comparisonPolicies',
  'policyDifference',
  'policyChangePercent',
] as const;

export type ComparisonColumnField = (typeof COMPARISON_COLUMN_FIELDS)[number];

export interface ComparisonColumnDefinition {
  field: ComparisonColumnField;
  kind: ComparisonColumnKind;
}

const COLUMN_KINDS: Readonly<Record<ComparisonColumnField, ComparisonColumnKind>> = {
  entityName: 'text',
  mgaName: 'text',
  goalAmount: 'currency',
  goalAttainedPercent: 'percent',
  currentPremium: 'currency',
  comparisonPremium: 'currency',
  premiumDifference: 'currency',
  premiumChangePercent: 'percent',
  currentPolicies: 'decimal',
  comparisonPolicies: 'decimal',
  policyDifference: 'decimal',
  policyChangePercent: 'percent',
};

/** Premium block then policy block, as every grid and every export reads. */
const FIGURE_FIELDS: readonly ComparisonColumnField[] = [
  'currentPremium',
  'comparisonPremium',
  'premiumDifference',
  'premiumChangePercent',
  'currentPolicies',
  'comparisonPolicies',
  'policyDifference',
  'policyChangePercent',
];

const GOAL_FIELDS: readonly ComparisonColumnField[] = ['goalAmount', 'goalAttainedPercent'];

/**
 * What the page-wide column picker offers: every column but the name, which identifies the row
 * and cannot be hidden anywhere — a table without it is a list of unattributed numbers.
 */
export const COMPARISON_HIDEABLE_FIELDS: readonly ComparisonColumnField[] = COMPARISON_COLUMN_FIELDS.filter(
  field => field !== 'entityName',
);

/**
 * The columns a comparison table can be ordered by, with the caption the order is named by.
 *
 * Numeric only. Sorting these tables by a name would say nothing about production, and every
 * request already settles ties by name.
 */
export const COMPARISON_SORT_CAPTIONS: Readonly<Record<string, string>> = {
  goalAmount: 'Goal',
  goalAttainedPercent: '% Goal Met',
  currentPremium: 'Current Premium',
  currentPolicies: 'Current Policies',
  comparisonPremium: 'Comparison Premium',
  comparisonPolicies: 'Comparison Policies',
  premiumDifference: 'Premium Diff',
  policyDifference: 'Policy Diff',
  premiumChangePercent: '% Premium Change',
  policyChangePercent: '% Policy Change',
};

export const COMPARISON_DEFAULT_SORT_FIELD: ComparisonColumnField = 'premiumDifference';

/**
 * Ties break by name, ascending, and the viewer never sets it.
 *
 * Whole ranges of rows share a value — every agency with no production last year has a
 * comparison premium of zero — so without it the order among them was arbitrary, and a row
 * could appear twice or not at all as a table grew.
 */
export const COMPARISON_SECONDARY_SORT = { selector: 'entityName', desc: false } as const;

/** How many rows a mover table opens with until the viewer picks otherwise. */
export const COMPARISON_DEFAULT_TOP_COUNT = 15;

export interface ComparisonSortDefinition {
  field: ComparisonColumnField;
  desc: boolean;
}

export interface ComparisonTableDefinition {
  /** The grid's id within the report, which is what a preset stores its settings under. */
  gridId: string;
  kind: ComparisonTableKind;
  dataset: ReportingComparisonDataset;
  /** What a row is counted by; the overall table has a row per group rather than a level. */
  level: ComparisonTableLevel | null;
  direction: ComparisonTableDirection | null;
  /** The name column's caption, which names what the rows are. */
  entityCaption: string;
  /** The rows in the plural, for a title: "Top 15 MGAs". */
  entityPlural: string;
  columns: readonly ComparisonColumnDefinition[];
  /** The order a table opens on; null for the overall table, which cannot be sorted. */
  defaultSort: ComparisonSortDefinition | null;
}

export function getComparisonOverallGridId(dataset: ReportingComparisonDataset): string {
  return `overall.${dataset}`;
}

export function getComparisonMoverGridId(
  dataset: ReportingComparisonDataset,
  level: ComparisonTableLevel,
  direction: ComparisonTableDirection,
): string {
  return `mover.${dataset}.${level}.${direction}`;
}

export function getComparisonAllGridId(dataset: ReportingComparisonDataset, level: ComparisonTableLevel): string {
  return `all.${dataset}.${level}`;
}

/**
 * What a table's rows are, named once — its column and its title had drifted apart.
 *
 * The rows are whatever the page is grouped by one level down, which is not the level's own
 * name: an MGA page's "mga" rows are that agency's agents.
 */
export function getComparisonEntityNames(
  scope: ReportingComparisonScope,
  level: ComparisonTableLevel,
): { caption: string; plural: string } {
  if (level === 'agent') {
    return { caption: 'Agent Name', plural: 'Agents' };
  }

  switch (scope) {
    case ReportingComparisonScopes.allianceGroup:
      return { caption: 'Agency Name', plural: 'MGAs' };
    case ReportingComparisonScopes.rmd:
      return { caption: 'RMD Name', plural: 'RMDs' };
    default:
      return { caption: 'Agent Name', plural: 'Agents' };
  }
}

/** Goals are set per agency for paid production, so only company-wide paid rows carry them. */
function hasGoalColumns(scope: ReportingComparisonScope, dataset: ReportingComparisonDataset): boolean {
  return scope === ReportingComparisonScopes.allianceGroup && dataset === ReportingComparisonDatasets.paid;
}

function toColumns(fields: readonly ComparisonColumnField[]): readonly ComparisonColumnDefinition[] {
  return fields.map(field => ({ field, kind: COLUMN_KINDS[field] }));
}

/** The overall row names its group rather than an agency or an agent, and paid carries goals. */
export function getComparisonOverallTable(
  scope: ReportingComparisonScope,
  dataset: ReportingComparisonDataset,
): ComparisonTableDefinition {
  return {
    gridId: getComparisonOverallGridId(dataset),
    kind: 'overall',
    dataset,
    level: null,
    direction: null,
    entityCaption: 'Agency Name',
    entityPlural: 'Agencies',
    columns: toColumns([
      'entityName',
      ...(dataset === ReportingComparisonDatasets.paid ? GOAL_FIELDS : []),
      ...FIGURE_FIELDS,
    ]),
    defaultSort: null,
  };
}

/**
 * A mover table: the same rows as the full list, read from one end.
 *
 * An agent table carries its MGA wherever it appears, and an RMD's MGA rows carry the agency they
 * belong to; only company-wide paid MGA rows carry goals.
 */
export function getComparisonMoverTable(
  scope: ReportingComparisonScope,
  dataset: ReportingComparisonDataset,
  level: ComparisonTableLevel,
  direction: ComparisonTableDirection,
): ComparisonTableDefinition {
  const names = getComparisonEntityNames(scope, level);
  const leading: ComparisonColumnField[] =
    level === 'agent'
      ? ['entityName', 'mgaName']
      : [
          'entityName',
          ...(scope === ReportingComparisonScopes.rmd ? (['mgaName'] as const) : []),
          ...(hasGoalColumns(scope, dataset) ? GOAL_FIELDS : []),
        ];

  return {
    gridId: getComparisonMoverGridId(dataset, level, direction),
    kind: 'mover',
    dataset,
    level,
    direction,
    entityCaption: names.caption,
    entityPlural: names.plural,
    columns: toColumns([...leading, ...FIGURE_FIELDS]),
    // Increases and decreases are the same rows read from opposite ends.
    defaultSort: { field: COMPARISON_DEFAULT_SORT_FIELD, desc: direction === 'increase' },
  };
}

/** A full-list table. MGAs are listed company-wide only; agents on the pages that have people. */
export function getComparisonAllTable(
  scope: ReportingComparisonScope,
  dataset: ReportingComparisonDataset,
  level: ComparisonTableLevel,
): ComparisonTableDefinition {
  const isAgentLevel = level === 'agent';
  const leading: ComparisonColumnField[] = isAgentLevel
    ? ['entityName', ...(scope === ReportingComparisonScopes.rmd ? (['mgaName'] as const) : [])]
    : ['entityName', ...(dataset === ReportingComparisonDatasets.paid ? GOAL_FIELDS : [])];

  return {
    gridId: getComparisonAllGridId(dataset, level),
    kind: 'all',
    dataset,
    level,
    direction: null,
    entityCaption: isAgentLevel ? 'Agent Name' : 'Agency Name',
    entityPlural: isAgentLevel ? 'Agents' : 'MGAs',
    columns: toColumns([...leading, ...FIGURE_FIELDS]),
    defaultSort: { field: COMPARISON_DEFAULT_SORT_FIELD, desc: true },
  };
}

/** Whether a scope's page shows a level's mover tables at all. */
export function hasComparisonMoverTables(scope: ReportingComparisonScope, level: ComparisonTableLevel): boolean {
  // An RMD's MGA-level rows are grouped by RMD — the viewer is one — so they say nothing; an MGA
  // page's rows are already its agents, so a second agent table would repeat them.
  return level === 'mga' ? scope !== ReportingComparisonScopes.rmd : scope !== ReportingComparisonScopes.mga;
}

const COMPARISON_DATASETS: readonly ReportingComparisonDataset[] = [
  ReportingComparisonDatasets.paid,
  ReportingComparisonDatasets.submit,
];

const DIRECTIONS: readonly ComparisonTableDirection[] = ['increase', 'decrease'];

/** Every table a scope's page can show, in the order the page shows them. */
export function getComparisonTables(scope: ReportingComparisonScope): ComparisonTableDefinition[] {
  return COMPARISON_DATASETS.flatMap(dataset => [
    getComparisonOverallTable(scope, dataset),
    ...(['mga', 'agent'] as const)
      .filter(level => hasComparisonMoverTables(scope, level))
      .flatMap(level => DIRECTIONS.map(direction => getComparisonMoverTable(scope, dataset, level, direction))),
    ...(scope === ReportingComparisonScopes.allianceGroup
      ? [getComparisonAllTable(scope, dataset, 'mga')]
      : [getComparisonAllTable(scope, dataset, 'agent')]),
  ]);
}

export function findComparisonTable(scope: ReportingComparisonScope, gridId: string): ComparisonTableDefinition | null {
  return getComparisonTables(scope).find(table => table.gridId === gridId) ?? null;
}

/**
 * Which grid features a column offers, so a stored filter is read the way the grid applied it.
 *
 * A mover's names are picked from a header filter rather than typed into a filter row, and its
 * figures the other way round. The full lists offer both on every column; the overall table
 * offers neither.
 */
export function getComparisonColumnFilterModes(
  table: ComparisonTableDefinition,
  column: ComparisonColumnDefinition,
): { filterRow: boolean; headerFilter: boolean } {
  switch (table.kind) {
    case 'mover':
      return column.kind === 'text'
        ? { filterRow: false, headerFilter: true }
        : { filterRow: true, headerFilter: false };
    case 'all':
      return { filterRow: true, headerFilter: true };
    default:
      return { filterRow: false, headerFilter: false };
  }
}

/** The filter operation a figure's filter row opens on: "at least", which is what these are read for. */
export const COMPARISON_DEFAULT_FILTER_OPERATION = '>=';

export function getComparisonColumnCaption(
  field: ComparisonColumnField,
  table: Pick<ComparisonTableDefinition, 'entityCaption'>,
  ranges: ComparisonRanges,
): string {
  switch (field) {
    case 'entityName':
      return table.entityCaption;
    case 'mgaName':
      return 'MGA';
    case 'goalAmount':
      return 'Goal';
    case 'goalAttainedPercent':
      return getComparisonPercentCaption('Goal Met');
    case 'currentPremium':
      return getComparisonMetricCaption('current', 'Premium', ranges);
    case 'comparisonPremium':
      return getComparisonMetricCaption('comparison', 'Premium', ranges);
    case 'premiumDifference':
      return 'Premium Diff';
    case 'premiumChangePercent':
      return getComparisonPercentCaption('Premium Change');
    case 'currentPolicies':
      return getComparisonMetricCaption('current', 'Policies', ranges);
    case 'comparisonPolicies':
      return getComparisonMetricCaption('comparison', 'Policies', ranges);
    case 'policyDifference':
      return 'Policy Diff';
    case 'policyChangePercent':
      return getComparisonPercentCaption('Policy Change');
  }
}

/**
 * The caption an order is named by, as its column's header reads it — the header says
 * "2026 Premium" where a fixed caption would say "Current Premium".
 */
export function getComparisonSortCaption(sortField: string, ranges: ComparisonRanges): string {
  if ((COMPARISON_COLUMN_FIELDS as readonly string[]).includes(sortField) && sortField !== 'entityName') {
    return getComparisonColumnCaption(sortField as ComparisonColumnField, { entityCaption: '' }, ranges);
  }

  return COMPARISON_SORT_CAPTIONS[sortField] ?? sortField;
}

/**
 * What a mover table is actually showing: how many rows, and the column they are ordered by.
 *
 * "Positive" and "Negative" name which end the rows are read from without claiming a movement:
 * a column like % Goal Met has no increase or decrease about it. A table with nothing in it is
 * not "Top 0" of anything, so the count only appears once there is one.
 */
export function getComparisonMoverTitle(
  table: Pick<ComparisonTableDefinition, 'entityPlural'>,
  rowCount: number,
  sort: { selector: string; desc: boolean },
  ranges: ComparisonRanges,
): string {
  const polarity = sort.desc ? 'Positive' : 'Negative';
  const top = rowCount > 0 ? `Top ${rowCount}` : 'Top';

  return `${top} ${table.entityPlural} sorted by ${polarity} ${getComparisonSortCaption(sort.selector, ranges)}`;
}

export function getComparisonTableTitle(table: ComparisonTableDefinition): string {
  switch (table.kind) {
    case 'overall':
      return 'Overall comparison';
    case 'all':
      return `All ${table.entityPlural} Production`;
    default:
      return `Top ${table.entityPlural}`;
  }
}

export function getComparisonDatasetTitle(dataset: ReportingComparisonDataset): string {
  return dataset === ReportingComparisonDatasets.paid ? 'Paid Production' : 'Submitted Production';
}

export interface ComparisonSummaryCard {
  label: string;
  value: string;
  tone?: ComparisonTone;
}

/**
 * The figures a dataset opens with.
 *
 * Goal progress belongs to paid production only, and how many are ahead of their goal means
 * nothing on an MGA's own page, where there is one agency to count.
 */
export function buildComparisonSummaryCards(
  summary: ReportingComparisonSummaryDto,
  options: { includeGoalMetrics: boolean; scope: ReportingComparisonScope; ranges: ComparisonRanges },
): ComparisonSummaryCard[] {
  return [
    {
      label: getComparisonMetricCaption('current', 'Premium', options.ranges),
      value: formatComparisonCurrency(summary.currentPremium),
    },
    {
      label: getComparisonMetricCaption('current', 'Policies', options.ranges),
      value: formatComparisonDecimal(summary.currentPolicies),
    },
    {
      label: 'Premium Diff',
      value: formatComparisonDeltaCurrency(summary.premiumDifference),
      tone: getComparisonTone(summary.premiumDifference),
    },
    {
      label: 'Policy Diff',
      value: formatComparisonDeltaNumber(summary.policyDifference),
      tone: getComparisonTone(summary.policyDifference),
    },
    ...(options.includeGoalMetrics
      ? [
          {
            label: getComparisonPercentCaption('Goal Met'),
            value: formatComparisonPercent(summary.goalAttainedPercent),
            tone: 'neutral' as const,
          },
          ...(options.scope === ReportingComparisonScopes.mga
            ? []
            : [
                {
                  label: 'Ahead / On Target',
                  value: summary.entitiesAheadOrOnTarget == null ? 'N/A' : `${summary.entitiesAheadOrOnTarget}`,
                  tone: 'neutral' as const,
                },
              ]),
        ]
      : []),
  ];
}

/** Whether a dataset's page carries agent movers: only when the comparison found any. */
export function hasComparisonAgentMovers(dataset: ReportingComparisonDatasetDto): boolean {
  return (dataset.topAgentIncreases?.length ?? 0) > 0 || (dataset.topAgentDecreases?.length ?? 0) > 0;
}

/* ------------------------------------------------------------------------------------------------
 * PDF blocks
 * --------------------------------------------------------------------------------------------- */

export type ComparisonPdfBlockKind = 'summary' | 'overall' | 'mover' | 'charts';

/**
 * One thing the PDF can print, and what a viewer ticks to have it printed.
 *
 * A block that prints a table is identified by that table's grid id, the key presets already
 * store it under, so a preset can one day name the blocks it prints without a second vocabulary.
 */
export interface ComparisonPdfBlockDefinition {
  id: string;
  dataset: ReportingComparisonDataset;
  kind: ComparisonPdfBlockKind;
  table: ComparisonTableDefinition | null;
  label: string;
}

export function getComparisonSummaryBlockId(dataset: ReportingComparisonDataset): string {
  return `summary.${dataset}`;
}

export function getComparisonChartsBlockId(dataset: ReportingComparisonDataset): string {
  return `charts.${dataset}`;
}

function describeMoverBlock(table: ComparisonTableDefinition): string {
  return `Top ${table.entityPlural} — ${table.direction === 'increase' ? 'positive' : 'negative'}`;
}

/**
 * Every block a scope's PDF can carry, in the order it prints them: each dataset in turn, as
 * the Excel workbook reads, with its cards, its tables and then its charts.
 *
 * The full lists are left out: they are Excel's, where a whole set can be sorted and filtered.
 */
export function getComparisonPdfBlocks(scope: ReportingComparisonScope): ComparisonPdfBlockDefinition[] {
  return COMPARISON_DATASETS.flatMap(dataset => {
    const overall = getComparisonOverallTable(scope, dataset);
    const movers = (['mga', 'agent'] as const)
      .filter(level => hasComparisonMoverTables(scope, level))
      .flatMap(level => DIRECTIONS.map(direction => getComparisonMoverTable(scope, dataset, level, direction)));

    return [
      {
        id: getComparisonSummaryBlockId(dataset),
        dataset,
        kind: 'summary' as const,
        table: null,
        label: 'Summary cards',
      },
      { id: overall.gridId, dataset, kind: 'overall' as const, table: overall, label: 'Overall comparison' },
      ...movers.map(table => ({
        id: table.gridId,
        dataset,
        kind: 'mover' as const,
        table,
        label: describeMoverBlock(table),
      })),
      { id: getComparisonChartsBlockId(dataset), dataset, kind: 'charts' as const, table: null, label: 'Charts' },
    ];
  });
}

/**
 * Whether a block has anything to print for this comparison.
 *
 * The same tests the page uses to decide what to draw: nothing prints for a dataset the API
 * could not lay out, agent movers only when the comparison found agents, and charts only when
 * there is a trend or a ranking to draw.
 */
export function isComparisonPdfBlockAvailable(
  block: ComparisonPdfBlockDefinition,
  dataset: ReportingComparisonDatasetDto,
): boolean {
  if (!dataset.canDisplayGrid) {
    return false;
  }

  if (block.kind === 'mover' && block.table?.level === 'agent') {
    return hasComparisonAgentMovers(dataset);
  }

  if (block.kind === 'charts') {
    return dataset.trend.length > 0 || dataset.ranking.length > 0;
  }

  return true;
}
